/**
 * The notes about a dataset, said once each.
 *
 * WHAT THIS REPLACED, and why it needed replacing: one line per NOTE, each headed by the field's
 * CODE. On Peak's Form dataset that is forty-odd lines, twenty-five of which say "File fields hold
 * a reference, not a value" one after another, against codes like `accreditationCertificate` and
 * `purchaseOrderCopy` that appear nowhere else in the screen. It reads as gibberish, and a reader
 * who skips it never learns the one or two facts in there that matter -- which is the whole purpose
 * of the panel.
 *
 * ONE LINE PER DISTINCT FACT, naming the fields it is true of. Twenty-five lines become one, and a
 * conflict nobody could see at the bottom of a wall is now one line of four.
 *
 * ORDERED BY WHAT A READER CAN ACT ON. A conflict means two forms disagree and somebody has to
 * decide; a File field being unreportable is a property of File fields. The first is worth reading
 * and the second is worth knowing once.
 */

export type DatasetNote = {kind: string; code?: string; label?: string; detail: string};

export type NoteLine = {
    kind: string;
    detail: string;
    /** The fields this is true of, in the order they arrived. */
    fields: string[];
};

/** Most-actionable first. Anything unrecognised sorts after the kinds we know about. */
const ORDER = ["conflict", "repeatingGroup", "multiValue", "skipped"];

const rank = (kind: string) => {
    const at = ORDER.indexOf(kind);
    return at === -1 ? ORDER.length : at;
};

export function groupNotes(notes: DatasetNote[]): NoteLine[] {
    const lines: NoteLine[] = [];
    const byKey = new Map<string, NoteLine>();

    for (const note of notes) {
        // A REPEATING GROUP NAMES ITSELF IN ITS OWN DETAIL ("a dataset of its own: Form > Steps"),
        // so those never collapse -- and should not. Everything else groups on the sentence.
        const key = `${note.kind}\u0000${note.detail}`;
        const name = note.label || note.code || "";
        const already = byKey.get(key);
        if (already) {
            if (name && !already.fields.includes(name)) already.fields.push(name);
            continue;
        }
        const line: NoteLine = {kind: note.kind, detail: note.detail,
                                fields: name ? [name] : []};
        byKey.set(key, line);
        lines.push(line);
    }

    // STABLE WITHIN A KIND: the server already sorted the fields, and re-sorting by sentence would
    // shuffle the repeating groups into an order that has nothing to do with the form.
    return lines.sort((a, b) => rank(a.kind) - rank(b.kind));
}

/** How one line reads. The fields come first when there are few enough to scan. */
export function noteText(line: NoteLine): string {
    if (line.fields.length === 0) return line.detail;
    if (line.fields.length === 1) return `${line.fields[0]}: ${line.detail}`;
    return `${line.fields.join(", ")} — ${line.detail}`;
}
